import type { ReactNode } from 'react';
import { X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Option {
  value: string;
  label: string;
  group?: string;
}

interface MultiSelectFieldProps {
  label: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  testId?: string;
  disabled?: boolean;
  /** Shown inside the label, after its text (for example where the value came from). */
  labelAdornment?: ReactNode;
}

export function MultiSelectField({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  testId,
  disabled = false,
  labelAdornment,
}: MultiSelectFieldProps): JSX.Element {
  const selectedOptions = options.filter((opt) => value.includes(opt.value));
  const availableOptions = options.filter((opt) => !value.includes(opt.value));

  const handleAdd = (optionValue: string) => {
    if (optionValue && !value.includes(optionValue)) {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  // Group options by category if available
  const groupedOptions = availableOptions.reduce<Record<string, Option[]>>((acc, opt) => {
    const group = opt.group ?? 'Other';
    acc[group] ??= [];
    acc[group].push(opt);
    return acc;
  }, {});

  const hasGroups = Object.keys(groupedOptions).length > 1 || !groupedOptions.Other;

  return (
    <div className="space-y-2">
      <Label className={labelAdornment ? 'flex items-center gap-2' : undefined}>
        {label}
        {labelAdornment}
      </Label>

      {/* Selected items */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1 pb-2">
          {selectedOptions.map((opt) => (
            <Badge key={opt.value} variant="secondary" className="gap-1">
              {opt.label}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => { handleRemove(opt.value); }}
                disabled={disabled}
                aria-label={`Remove ${opt.label}`}
                data-testid={testId === undefined ? undefined : `${testId}-remove-${opt.value}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add selector */}
      <Select onValueChange={handleAdd} value="" disabled={disabled}>
        <SelectTrigger data-testid={testId} disabled={disabled}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {hasGroups ? (
            Object.entries(groupedOptions).map(([group, opts]) => (
              <div key={group}>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {group}
                </div>
                {opts.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </div>
            ))
          ) : (
            availableOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))
          )}
          {availableOptions.length === 0 && (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              All options selected
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
